import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  Button,
  buttonClass,
  DataTable,
  ErrorState,
  Icon,
  LoadingState,
  TableEmpty,
  type Column,
} from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { IntegrationLogListItem } from "@/lib/api/types";
import { useStoreOptionsQuery } from "@/shared/stores/services/queries/useStoreOptionsQuery";
import { useIntegrationLogsQuery } from "../services/queries/useIntegrationLogsQuery";
import {
  buildIntegrationLogQuery,
  countActiveIntegrationLogFilters,
} from "../services/integration-log-search-params";
import {
  EMPTY_CELL,
  formatDurationMs,
  formatLogTimestamp,
} from "../services/integration-log-format";
import { IntegrationLogFilters } from "../components/IntegrationLogFilters";
import {
  IntegrationLogDirectionChip,
  IntegrationLogStatusChip,
} from "../components/IntegrationLogChips";
import { IntegrationLogReference } from "../components/IntegrationLogReference";
import { IntegrationLogDetailDrawer } from "../components/IntegrationLogDetailDrawer";
import type {
  IntegrationLogFilterValues,
  IntegrationLogsPageProps,
} from "../types/integration-logs.types";

export function IntegrationLogsPage({
  locale,
  limit,
  offset,
  search,
  storeId,
  status,
  direction,
  provider,
  dateFrom,
  dateTo,
}: IntegrationLogsPageProps) {
  const t = useTranslations();
  const uiLocale = useLocale();
  const navigate = useNavigate();
  // Bumped when the table clears the filters, so the filter bar drops its
  // local field state instead of re-applying the old values.
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const filters: IntegrationLogFilterValues = {
    search,
    storeId,
    status,
    direction,
    provider,
    dateFrom,
    dateTo,
  };

  const { data, isLoading, error, refetch } = useIntegrationLogsQuery({
    limit,
    offset,
    ...filters,
  });
  const { data: storesData } = useStoreOptionsQuery();

  const basePath = `/${locale}/admin/integration-logs`;

  const columns: Column<IntegrationLogListItem>[] = [
    {
      key: "occurredAt",
      label: t(keys.integrationLogs.columnTime),
      width: 1.1,
      render: (log) => (
        <span className="rd-num" style={{ color: "var(--rd-text-secondary)", fontSize: 13 }}>
          {formatLogTimestamp(log.occurredAt, locale)}
        </span>
      ),
    },
    {
      key: "direction",
      label: t(keys.integrationLogs.columnDirection),
      width: 0.8,
      render: (log) => <IntegrationLogDirectionChip direction={log.direction} />,
    },
    {
      key: "provider",
      label: t(keys.integrationLogs.columnProvider),
      width: 0.8,
      render: (log) => <span style={{ ...truncate, fontWeight: 600 }}>{log.provider}</span>,
    },
    {
      key: "operation",
      label: t(keys.integrationLogs.columnOperation),
      width: 1.1,
      render: (log) => (
        <span dir="ltr" style={{ ...truncate, ...mono }}>
          {log.operation || EMPTY_CELL}
        </span>
      ),
    },
    {
      key: "store",
      label: t(keys.integrationLogs.columnStore),
      width: 1.1,
      render: (log) => <span style={truncate}>{log.storeName || EMPTY_CELL}</span>,
    },
    {
      key: "reference",
      label: t(keys.integrationLogs.columnReference),
      width: 1.1,
      render: (log) => (
        <IntegrationLogReference
          locale={locale}
          reference={log.reference}
          entityType={log.entityType}
          entityId={log.entityId}
          storeId={log.storeId}
          stopRowPropagation
        />
      ),
    },
    {
      key: "status",
      label: t(keys.integrationLogs.columnStatus),
      width: 0.9,
      render: (log) => <IntegrationLogStatusChip status={log.status} />,
    },
    {
      key: "httpStatus",
      label: t(keys.integrationLogs.columnHttpStatus),
      width: 0.5,
      align: "end",
      numeric: true,
      render: (log) => (
        <span
          style={{
            color:
              log.httpStatus !== null && log.httpStatus >= 400
                ? "var(--rd-red)"
                : "var(--rd-text-secondary)",
          }}
        >
          {log.httpStatus === null ? EMPTY_CELL : log.httpStatus}
        </span>
      ),
    },
    {
      key: "durationMs",
      label: t(keys.integrationLogs.columnDuration),
      width: 0.7,
      align: "end",
      numeric: true,
      render: (log) => (
        <span style={{ color: "var(--rd-text-secondary)" }}>
          {formatDurationMs(log.durationMs, t(keys.integrationLogs.durationSuffix))}
        </span>
      ),
    },
  ];

  const page = t(keys.integrationLogs.title).toLocaleLowerCase(uiLocale);
  const filtered = countActiveIntegrationLogFilters(filters) > 0;
  const clearFilters = () => {
    navigate(`${basePath}?${buildIntegrationLogQuery({ limit, offset: 0, filters: {} })}`, { replace: true });
    setFiltersKey((value) => value + 1);
  };
  const filterBar = (
    <IntegrationLogFilters
      key={filtersKey}
      basePath={basePath}
      limit={limit}
      locale={locale}
      initialFilters={filters}
      storeOptions={storesData?.items ?? []}
    />
  );

  if (isLoading || error || !data) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {filterBar}
        {isLoading ? (
          <LoadingState
            title={t(keys.shell.loadingTitle, { page })}
            sub={t(keys.shell.loadingSub)}
            rows={Math.min(limit, 6)}
          />
        ) : (
          <ErrorState
            title={t(keys.shell.errorTitle, { page })}
            sub={t(keys.shell.errorSub)}
            onRetry={() => void refetch()}
            retryLabel={t(keys.shell.retry)}
          />
        )}
      </div>
    );
  }

  const hasPrev = offset > 0;
  const hasNext = offset + limit < data.total;
  const prevOffset = Math.max(offset - limit, 0);
  const nextOffset = offset + limit;
  const number = (value: number) =>
    value.toLocaleString(locale === "ar" ? "ar-EG" : "en-US");
  const from = data.total && data.items.length ? offset + 1 : 0;
  const to = offset + data.items.length;

  const pageLink = (target: number, label: string, isNext: boolean) => {
    const glyph = <Icon icon={isNext ? ChevronRight : ChevronLeft} size={14} className="rd-flip-rtl" />;
    const content = isNext ? (
      <>
        <span>{label}</span>
        {glyph}
      </>
    ) : (
      <>
        {glyph}
        <span>{label}</span>
      </>
    );
    const enabled = isNext ? hasNext : hasPrev;
    return enabled ? (
      <Link
        to={`${basePath}?${buildIntegrationLogQuery({ limit, offset: target, filters })}`}
        className={buttonClass("ghost", "sm")}
      >
        {content}
      </Link>
    ) : (
      <span className={buttonClass("ghost", "sm")} aria-disabled="true" style={{ opacity: 0.45, pointerEvents: "none" }}>
        {content}
      </span>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {filterBar}

      <DataTable
        rows={data.items}
        columns={columns}
        rowKey={(log) => log.id}
        minWidth={980}
        label={t(keys.integrationLogs.title)}
        onRowClick={(log) => setSelectedLogId(log.id)}
        empty={
          filtered ? (
            <TableEmpty
              illustration="admin-no-results"
              title={t("integrationLogs.noResultsTitle")}
              actions={
                <Button variant="secondary" size="sm" icon={X} onClick={clearFilters}>
                  {t(keys.common.clearFilters)}
                </Button>
              }
            >
              {t("integrationLogs.noResultsText")}
            </TableEmpty>
          ) : (
            <TableEmpty illustration="admin-empty-table" title={t("integrationLogs.firstUseTitle")}>
              {t("integrationLogs.firstUseText")}
            </TableEmpty>
          )
        }
        footer={
          <div className="rd-table-foot" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            {pageLink(prevOffset, t(keys.common.previous), false)}
            <span className="rd-num" style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
              {t(keys.common.paginationSummary, {
                from: number(from),
                to: number(to),
                total: number(data.total),
              })}
            </span>
            {pageLink(nextOffset, t(keys.common.next), true)}
          </div>
        }
      />

      <IntegrationLogDetailDrawer
        locale={locale}
        logId={selectedLogId}
        onClose={() => setSelectedLogId(null)}
      />
    </div>
  );
}

const truncate = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const mono = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
} as const;
